import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { RankRewards } from '../RankRewards';
import { ScoreRewards } from '../ScoreRewards';
import { ThresholdRewards } from '../ThresholdRewards';
import { LeaderboardRewards_so5Leaderboard } from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: LeaderboardRewards_so5Leaderboard;
  withSeparator?: boolean;
  hideThresholds?: boolean;
} & Pick<ComponentProps<typeof RankRewards>, 'onClickRanking'>;

export const LeaderboardRewards = ({
  so5Leaderboard,
  withSeparator,
  hideThresholds,
  onClickRanking,
}: Props) => {
  const hasRankRewards = !!(so5Leaderboard.rewardsConfig.ranking || []).length;
  const hasScoreRewards = !!(so5Leaderboard.rewardsConfig.conditional || [])
    .length;
  const hasTargetRewards = !!(so5Leaderboard.rewardsConfig.targetable || [])
    .length;

  const hasMultipleTypeRewards =
    [hasRankRewards, hasScoreRewards, hasTargetRewards].filter(Boolean).length >
    1;

  return (
    <Vertical gap={2}>
      {hasTargetRewards && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerTarget} />
            </BodyS>
          )}
          <ScoreRewards
            targetable
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.targetable || []}
            so5League={so5Leaderboard.so5League}
          />
        </>
      )}
      {hasScoreRewards && !hasTargetRewards && !hideThresholds && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerThreshold} />
            </BodyS>
          )}
          <ThresholdRewards
            hideCompletionStatus
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.conditional || []}
            so5League={so5Leaderboard.so5League}
          />
        </>
      )}
      {hasRankRewards && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerRank} />
            </BodyS>
          )}
          <RankRewards
            so5Leaderboard={so5Leaderboard}
            withSeparator={withSeparator}
            onClickRanking={onClickRanking}
          />
        </>
      )}
    </Vertical>
  );
};

LeaderboardRewards.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardRewards_so5Leaderboard on So5Leaderboard {
      slug
      so5League {
        slug
        ...ScoreRewards_so5League
        ...ThresholdRewards_so5League
      }
      rewardsConfig {
        ranking {
          ranks
        }
        conditional {
          ...ThresholdRewards_so5RewardConfig
        }
        targetable {
          ...ScoreRewards_so5RewardConfig
        }
        unlockable {
          score
        }
      }
      ...RankRewards_so5Leaderboard
    }
    ${RankRewards.fragments.so5Leaderboard}
    ${ScoreRewards.fragments.so5League}
    ${ThresholdRewards.fragments.so5RewardConfig}
    ${ThresholdRewards.fragments.so5League}
    ${ScoreRewards.fragments.so5RewardConfig}
  ` as TypedDocumentNode<LeaderboardRewards_so5Leaderboard>,
};
